/**
 * Sticky product form that appears on scroll
 * Shows a sticky add-to-cart bar at the bottom when user scrolls past product details
 */

export class StickyProductForm extends HTMLElement {
  static TRANSITION = 'transform 0.3s ease'
  static UI_VISIBLE_SELECTOR = '[data-ui~="visible"]'

  lastScrollY: number
  ticking: boolean
  isVisible: boolean
  isScrolled: boolean
  bottomOffset: number
  elementHeight: number
  stickyFormElement: HTMLElement | null
  /** Cached scroll threshold — see `measureLayout`. */
  uiThreshold: number

  constructor() {
    super()

    // State only. A custom element constructor must not inspect layout or children —
    // they are not guaranteed to exist yet; `connectedCallback` does the measuring.
    this.lastScrollY = window.scrollY
    this.ticking = false
    this.isVisible = false
    this.isScrolled = false
    this.bottomOffset = 0
    this.elementHeight = 0
    this.stickyFormElement = null
    this.uiThreshold = window.innerHeight * 1.3

    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
  }

  get hiddenPosition() {
    return 'translateY(100%)'
  }

  get visiblePosition() {
    return 'translateY(-100%)'
  }

  /**
   * Measured on connect and resize, never inside the scroll rAF: the threshold only
   * changes when layout does, and querying/measuring `.product-main` per frame
   * forces a style + layout flush on every scroll tick.
   */
  measureLayout() {
    const productMain = document.querySelector('.product-main')
    this.uiThreshold = productMain
      ? (productMain.getBoundingClientRect().height / 4) * 3
      : window.innerHeight * 1.3

    this.bottomOffset = parseInt(window.getComputedStyle(this).bottom.replace('px', ''))
    this.stickyFormElement = this.querySelector('#sticky-product-form')
    this.elementHeight = this.stickyFormElement
      ? this.stickyFormElement.getBoundingClientRect().height
      : this.getBoundingClientRect().height
  }

  handleResize() {
    this.measureLayout()
  }

  connectedCallback() {
    this.style.transition = StickyProductForm.TRANSITION

    this.measureLayout()

    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.handleResize, { passive: true })

    // Start hidden
    this.style.transform = this.hiddenPosition
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
  }

  showStickyForm() {
    if (!this.isVisible) {
      this.style.transform = this.visiblePosition
      this.isVisible = true
      this.setAttribute('data-visible', 'visible')
    }
  }

  hideStickyForm() {
    if (this.isVisible) {
      this.style.transform = this.hiddenPosition
      this.isVisible = false
      this.removeAttribute('data-visible')
    }
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        const currentScroll = window.scrollY

        // Show sticky bar if we're past the threshold
        if (currentScroll > this.uiThreshold) {
          this.showStickyForm()
        } else {
          this.hideStickyForm()
        }
        this.lastScrollY = currentScroll
        this.ticking = false
      })

      this.ticking = true
    }
  }
}
